import * as React from 'react'
import {
  invalidateDomainTopic,
  subscribeDomainEvents,
  useAppDispatch,
  type DomainEventTopic
} from '@axilon-apps/domains-ontology-ui-state'

import { useDocumentVisible } from '../../hooks/use-document-visible'

/** Topics with consumers; a topic is added here when its surfaces move off polling. */
const REQUESTED_TOPICS: readonly DomainEventTopic[] = [
  'jobs',
  'runs',
  'agent-writes',
  'merges',
  'tasks',
  'chat-queue'
]
const THROTTLE_MS = 2_000

/**
 * Leading and trailing per-topic throttle: the first wake-up fires at once, and
 * later ones inside the window collapse into one call at its end.
 */
function createTopicThrottle(fire: (topic: DomainEventTopic) => void, windowMs: number) {
  const windows = new Map<
    DomainEventTopic,
    { pending: boolean; timer: ReturnType<typeof setTimeout> }
  >()
  const close = (topic: DomainEventTopic) => {
    const entry = windows.get(topic)
    if (!entry?.pending) {
      windows.delete(topic)
      return
    }
    entry.pending = false
    fire(topic)
    entry.timer = setTimeout(() => close(topic), windowMs)
  }
  return {
    trigger(topic: DomainEventTopic) {
      const entry = windows.get(topic)
      if (entry) {
        entry.pending = true
        return
      }
      fire(topic)
      windows.set(topic, { pending: false, timer: setTimeout(() => close(topic), windowMs) })
    },
    cancel() {
      for (const entry of windows.values()) clearTimeout(entry.timer)
      windows.clear()
    }
  }
}

/** One organization-wide domain events stream per visible tab; wake-ups refetch whole topics. */
export function EventStreamProvider() {
  const dispatch = useAppDispatch()
  const visible = useDocumentVisible()

  React.useEffect(() => {
    if (!visible) return

    const controller = new AbortController()
    const throttle = createTopicThrottle(
      (topic) => invalidateDomainTopic(dispatch, topic),
      THROTTLE_MS
    )
    let granted = REQUESTED_TOPICS
    void subscribeDomainEvents({
      topics: REQUESTED_TOPICS,
      onReady: (topics) => {
        granted = topics
        for (const topic of granted) throttle.trigger(topic)
      },
      onSignal: (topic) => {
        if (granted.includes(topic)) throttle.trigger(topic)
      },
      onResyncAll: () => {
        for (const topic of granted) throttle.trigger(topic)
      },
      onStatus: () => undefined,
      dispatch,
      signal: controller.signal
    })
    return () => {
      controller.abort()
      throttle.cancel()
    }
  }, [dispatch, visible])

  return null
}
